var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { attr, FASTElement, observable } from "@microsoft/fast-element";
import { isDropdownOption } from "../option/index.js";
/**
 * OptionGroup
 * @summary An OptionGroup Custom HTML Element that groups related options within a listbox or dropdown.
 * Implements semantics similar to the native {@link https://developer.mozilla.org/docs/Web/HTML/Element/optgroup | HTMLOptGroupElement}.
 *
 * @example
 * ```html
 * <fabric-listbox>
 *   <fabric-option-group label="Fruits">
 *     <fabric-option value="apple">Apple</fabric-option>
 *     <fabric-option value="banana">Banana</fabric-option>
 *   </fabric-option-group>
 *   <fabric-option-group label="Vegetables" disabled>
 *     <fabric-option value="carrot">Carrot</fabric-option>
 *     <fabric-option value="broccoli">Broccoli</fabric-option>
 *   </fabric-option-group>
 * </fabric-listbox>
 * ```
 *
 * @attr {string} label - The label for the option group, displayed as a non-selectable header.
 * @attr {boolean} disabled - When true, disables all options within this group.
 *
 * @prop {DropdownOption[]} options - The collection of child option elements.
 *
 * @slot - Default slot for fabric-option elements.
 *
 * @csspart label - The label element for the group header.
 *
 * @extends FASTElement
 * @tagname fabric-option-group
 * @public
 */
export class OptionGroup extends FASTElement {
    constructor() {
        super(...arguments);
        /**
         * The internal {@link https://developer.mozilla.org/docs/Web/API/ElementInternals | ElementInternals} instance for the component.
         *
         * @internal
         */
        this.elementInternals = this.attachInternals();
        /**
         * The collection of slotted option elements.
         *
         * @internal
         */
        this.slottedOptions = [];
    }
    /**
     * Gets the collection of child option elements.
     *
     * @public
     */
    get options() {
        return this.slottedOptions.filter((el) => {
            return isDropdownOption(el);
        });
    }
    /**
     * Handles changes to the disabled attribute.
     * Updates the aria-disabled state on the element internals.
     *
     * @param prev - The previous disabled state.
     * @param next - The new disabled state.
     * @internal
     */
    disabledChanged(prev, next) {
        this.elementInternals.ariaDisabled = next ? "true" : "false";
    }
    connectedCallback() {
        super.connectedCallback();
        this.elementInternals.role = "group";
    }
    /**
     * Handles changes to the label attribute.
     * Updates the aria-label for accessibility.
     *
     * @param _prev - The previous label.
     * @param next - The new label.
     * @internal
     */
    labelChanged(_prev, next) {
        this.elementInternals.ariaLabel = next !== null && next !== void 0 ? next : null;
    }
}
__decorate([
    attr
], OptionGroup.prototype, "label", void 0);
__decorate([
    attr({ mode: "boolean" })
], OptionGroup.prototype, "disabled", void 0);
__decorate([
    observable
], OptionGroup.prototype, "slottedOptions", void 0);
//# sourceMappingURL=option-group.js.map